<script setup lang="ts">
import { formatToAddressSystem } from '@/utils/formatToFullVisibleAddress'

import {
  StateOfAutocomplete,
  TIME_OF_DEBOUNCE,
} from '@/constants/components/autocomplete'
import { STATE_MENU_ADDRESS_SYSTEM } from '@/constants/statesOfAddressSystem'

import { useAutocompleteAddress } from '@/composables/address-system/useAutocompleteAddress'

import {
  useAddressSystemStateStore,
  useAddressSystemStore,
} from '@/store/addressSystem'
import { PlacesSearch } from '@/store/delivery/delivery.types'

import AddressSystemMenu from '@/components/AddressSystem/menu/AddressSystemMenu.vue'
import AddressSystemSearchInput from '@/components/AddressSystem/search/AddressSystemSearchInput.vue'

import type { SuggestedOfAddress } from '@/api/modules/geo/geocode.types'
import { debounce } from 'throttle-debounce'

const { $metrics } = useNuxtApp()

const addressSystemStore = useAddressSystemStore()

const { setDeliveryAddress, resetDeliveryAddress } = useAddressSystemStore()

const { changeMenuState } = useAddressSystemStateStore()

const {
  searchSuggestions,
  isRequestValid,
  searchSuggestionsError,
  searchSuggestionsLoading,
  getAddressInformation,
} = useAutocompleteAddress()

const requestText = ref<string | null>(null)
const selectedAutocompleteText = ref<string | null>(null)

const autocompleteList = ref<SuggestedOfAddress[]>([])

const onResetValues = () => {
  requestText.value = null
  searchSuggestionsError.value = ''
  selectedAutocompleteText.value = null
  resetDeliveryAddress(PlacesSearch.AUTOCOMPLETE)

  changeMenuState(STATE_MENU_ADDRESS_SYSTEM.DEFAULT)

  $metrics?.eventSendGoal({ ym: 'address_input_cleared' })
}

const setAutocompleteValue = (value: string) => {
  requestText.value = value
  selectedAutocompleteText.value = value
}

// Фиксируем ввод строки
const handleSearchInput = (value: string) => {
  setAutocompleteValue(value)

  if (!isRequestValid(value)) return

  debounceSearchSuggestion(requestText.value)
}

const onSearchSuggestions = async (
  request: string | null,
  levelTwo = false,
) => {
  $metrics?.eventSendGoal({ ym: 'address_input_started' })

  const result = await searchSuggestions(request)

  // TODO: В будущем продумать, как обрабатывать двухуровневый поиск для пользователя
  autocompleteList.value = levelTwo
    ? result.filter(({ value }) => value !== request)
    : result

  let stateSystemMenu = STATE_MENU_ADDRESS_SYSTEM.SUGGESTIONS

  if (!autocompleteList.value.length || searchSuggestionsError.value) {
    stateSystemMenu = STATE_MENU_ADDRESS_SYSTEM.ERROR
    $metrics?.eventSendGoal({ ym: 'address_input_error' })
  }

  changeMenuState(stateSystemMenu)
}

// Задержка запроса списка подсказок
const debounceSearchSuggestion = debounce(TIME_OF_DEBOUNCE, onSearchSuggestions)

const onUpdateFieldAddress = (
  selectedAddress: string,
  message: string = '',
) => {
  selectedAutocompleteText.value = selectedAddress
  requestText.value = selectedAddress

  searchSuggestionsError.value = message
}

// Метод обновления списка подсказок по выбранной подсказке
const onUpdateMenuSuggestions = (suggest: SuggestedOfAddress) => {
  onSearchSuggestions(suggest.value, true)
  onUpdateFieldAddress(suggest.value)
}

// В случае ошибки геопозиции сбрасываем поле и отображаем интерфейс
const onHandleErrorGeoPosition = () => {
  changeMenuState(STATE_MENU_ADDRESS_SYSTEM.NOT_GEO)
  onUpdateFieldAddress('')
}

// Устанавливаем значение адреса доставки в автокомплит и на карту для неавторизованного пользователя
const setAutocompleteSuggestion = () => {
  const deliveryAddress = addressSystemStore.deliveryAddress

  if (!deliveryAddress?.address?.address?.value) return

  const orderAddress =
    addressSystemStore.deliveryWidget?.order_address ||
    deliveryAddress?.address?.address?.value

  setAutocompleteValue(orderAddress)

  // Так как карта обновляется при смене стейта, необходимо его сбросить и установить заново
  resetDeliveryAddress(PlacesSearch.AUTOCOMPLETE)

  setTimeout(() => {
    setDeliveryAddress(deliveryAddress)
    changeMenuState(STATE_MENU_ADDRESS_SYSTEM.SELECTED)
  }, 100)
}

// Отслеживаем изменение адреса доставки для случаев клика по карте
watch(
  () => addressSystemStore.deliveryAddress.address,
  (newDeliveryAddress) => {
    const { address, place } = newDeliveryAddress

    // Кейс, когда через автокомплит выставили адрес, после чего выбрали адрес на карте,
    // Но возникла ошибка и скрыли балун, то сбрасываем значение и в автокомплите
    if (place === PlacesSearch.MAP && !address) onResetValues()

    // Обновляем значение в автокомплите только когда кликнули по карте
    if (place !== PlacesSearch.MAP || !address) return

    const { city, street, house } = address

    // В автокомплит вставляем только Город, улица, дом
    const inputAddress =
      formatToAddressSystem(city, street, house) || address.value

    // Проверка наличия дома у адреса
    const message = house ? '' : StateOfAutocomplete.NOT_VALID_ADDRESS

    onUpdateFieldAddress(inputAddress, message)

    // Если адрес вне доставки
    if (!addressSystemStore.deliveryAddress.details) {
      changeMenuState(STATE_MENU_ADDRESS_SYSTEM.NOT_DELIVERY)
      return
    }

    // Если адрес доставки в зоне
    changeMenuState(STATE_MENU_ADDRESS_SYSTEM.SELECTED)
  },
)

onMounted(() => setAutocompleteSuggestion())
</script>

<template>
  <div class="search-wrapper">
    <keep-alive>
      <address-system-search-input
        class="search-wrapper__input"
        :search-input-text="requestText"
        :selected-autocomplete-text="selectedAutocompleteText"
        :error-messages="searchSuggestionsError"
        @update:search="handleSearchInput"
        @on-reset-value="onResetValues"
        @on-update-field-address="onUpdateFieldAddress"
        @on-update-address-info="getAddressInformation"
        @on-handle-error-geo-position="onHandleErrorGeoPosition"
      />
    </keep-alive>

    <address-system-menu
      class="search-wrapper__menu"
      :suggestions="autocompleteList"
      :loading="searchSuggestionsLoading"
      @on-update-field-address="onUpdateFieldAddress"
      @on-update-address-info="getAddressInformation"
      @on-update-menu-suggestions="onUpdateMenuSuggestions"
    />
  </div>
</template>

<style scoped lang="scss">
.search-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

  &__menu {
    position: relative;
    height: 316px;
  }
}
</style>
